/**
 * 轻量实体-组件模型。
 * 实体只是一个 id + 若干组件对象；系统负责读写组件。
 * 不做 ECS 的原型/原型位掩码优化，保持可读与可扩展。
 */
let _nextId = 1;
export function nextEntityId() { return _nextId++; }

export class Entity {
  constructor(components = {}, tags = []) {
    this.id = nextEntityId();
    this.tags = new Set(tags);
    this.dead = false;
    Object.assign(this, components);
  }

  has(...names) { return names.every((n) => this[n] !== undefined); }
  add(name, comp) { this[name] = comp; return this; }
  remove(name) { delete this[name]; return this; }
  addTag(t) { this.tags.add(t); return this; }
  hasTag(t) { return this.tags.has(t); }
}

/**
 * 场景 = 瓦片地图 + 实体集合 + 系统列表。
 * 系统签名：{ name, update(dt, ctx), render?(ctx, view) }
 */
export class Scene {
  constructor(def = {}) {
    this.id = def.id || 'scene';
    this.name = def.name || '未命名场景';
    this.tilemap = null;
    this.entities = new Map();
    this.systems = [];
    this.props = [];          // 静态道具/家具（不进实体系统，单独渲染与排序）
    this.spawn = def.spawn || { x: 0, y: 0 };
    this.loaded = false;
  }

  setTilemap(tm) {
    this.tilemap = tm;
    this.spawn = tm.spawn || this.spawn;
  }

  add(entity) {
    this.entities.set(entity.id, entity);
    return entity;
  }

  remove(entityOrId) {
    const id = typeof entityOrId === 'number' ? entityOrId : entityOrId.id;
    const e = this.entities.get(id);
    if (e) e.dead = true;
    this.entities.delete(id);
  }

  get(id) { return this.entities.get(id); }

  /** 按组件或标签查询：query('transform', 'player') */
  query(...requires) {
    const out = [];
    for (const e of this.entities.values()) {
      if (e.dead) continue;
      let ok = true;
      for (const r of requires) {
        if (r.startsWith('#')) { if (!e.hasTag(r.slice(1))) { ok = false; break; } }
        else if (!e.has(r)) { ok = false; break; }
      }
      if (ok) out.push(e);
    }
    return out;
  }

  first(...requires) { return this.query(...requires)[0] || null; }

  /**
   * 挂一个系统。同一个系统只挂一次：进门会重跑一次 build，里头的六件套是模块级常量，
   * 不去重的话每进一次门就多挂一份，移动就会变成两倍速、时钟也会飞转。
   * 先按对象判，再按名字判（换过对象但同名的系统也别叠上去）。
   */
  use(system) {
    if (!system) return this;
    if (this.systems.indexOf(system) >= 0) return this;
    if (system.name && this.systems.some((s) => s.name === system.name)) return this;
    this.systems.push(system);
    return this;
  }

  /** 清理已删除实体 */
  gc() {
    for (const [id, e] of [...this.entities]) if (e.dead) this.entities.delete(id);
  }
}
